import type { DiretoriaIndicator } from '@/data/diretoriaTypes';

export interface DiretoriaFaturamentoBrutoPayload {
  ytdTotal: number;
  mesAtual: number;
  variacaoPct: number | null;
  mensal?: Array<{ mes: number; name: string; totalPago: number }>;
  porEmpresa?: Array<{ nomeEmpresa: string; totalPago: number }>;
  querySql?: string;
}

export interface DiretoriaInadimplenciaPayload {
  saldoVencido: number;
  titulosVencidos: number;
  novosMes: number;
  pessoasDistintas?: number;
  mediaTitulosPorPessoa?: number | null;
  faixas?: Array<{ faixa: string; qtd: number; saldo: number }>;
  porEmpresa?: Array<{
    empresa: string;
    count: number;
    valor: number;
    pessoas?: number;
    mediaTitulosPorPessoa?: number | null;
  }>;
  querySql?: string;
}

export interface DiretoriaInfracoesPayload {
  pagasMes?: { qtd: number; valor: number };
  vencidas?: { qtd: number; valor: number };
  aVencer?: { qtd: number; valor: number };
  mensal?: Array<{ name: string; qtdPagas: number; valorPagas: number }>;
  porEmpresa?: Array<{
    nomeEmpresa: string;
    qtdPagas: number;
    qtdVencidas: number;
    qtdAVencer: number;
    valorPagas: number;
    valorVencidas?: number;
    valorAVencer?: number;
    valorAberto: number;
  }>;
  querySql?: string;
}

export interface DiretoriaMotosAlugadasPayload {
  ativas: number;
  novasMes: number;
  canceladasMes: number;
  mensal?: Array<{ name: string; novas: number; canceladas: number }>;
  variacaoPct?: number | null;
  querySql?: string;
}

export interface DiretoriaRentabilidadePayload {
  receitaTotal: number;
  custoTotal: number;
  motosBase: number;
  mediaReceitaPorMoto: number;
  mediaCustoPorMoto: number;
  margemPct: number | null;
  mensal?: Array<{ name: string; receita: number; custo: number }>;
  variacaoPct?: number | null;
  querySql?: string;
}

export interface DiretoriaLocacaoLocalPayload {
  totalLocacoes: number;
  cidadesDistintas: number;
  topCidade: string | null;
  contratosMes: number;
  variacaoPct?: number | null;
  itens?: Array<{ bairro: string; cidade: string; quantidade: number }>;
  querySql?: string;
}

const emptyEstado = { qtd: 0, valor: 0 };

const fmtPct = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1).replace('.', ',')}%`;
};

const fmtMi = (value: number, formatCurrency: (n: number) => string): string => {
  if (Math.abs(value) >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(2).replace('.', ',')} mi`;
  }
  return formatCurrency(value);
};

const fmtQtdValor = (qtd: number, valor: number, formatCurrency: (n: number) => string): string =>
  `${qtd} · ${formatCurrency(valor)}`;

function hasChartOrTable(
  series: Array<{ name: string; valor: number; valor2?: number }>,
  tableRows: string[][],
  pieData?: Array<{ name: string; value: number }>
): boolean {
  return series.length > 0 || tableRows.length > 0 || (pieData?.length ?? 0) > 0;
}

export function buildFaturamentoBrutoIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaFaturamentoBrutoPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const mensal = payload.mensal ?? [];
  const porEmpresa = payload.porEmpresa ?? [];
  const series = mensal.map((row) => ({ name: row.name, valor: Math.round(row.totalPago) }));
  const tableRows = porEmpresa
    .slice(0, 20)
    .map((row) => [row.nomeEmpresa, formatCurrency(row.totalPago)]);
  return {
    ...base,
    shortDescription: 'Faturamento bruto (baixas a receber) — ERP FlyStart.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Anual (YTD)',
        value: fmtMi(payload.ytdTotal ?? 0, formatCurrency),
        hint: 'Ano de dataTo · ERP FlyStart',
        tone: (payload.ytdTotal ?? 0) > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Período / mês ref.',
        value: formatCurrency(payload.mesAtual ?? 0),
        hint: 'Baixas no intervalo (ou mês de dataTo)',
        tone: (payload.mesAtual ?? 0) > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Var. vs período ant.',
        value: fmtPct(payload.variacaoPct ?? null),
        hint: 'ERP FlyStart',
        tone:
          payload.variacaoPct == null ? 'neutral' : payload.variacaoPct >= 0 ? 'good' : 'bad',
      },
    ],
    chartKind: 'line',
    chartTitle: 'Evolução mensal (ano de referência)',
    series,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Faturamento' },
    tableHeaders: ['Unidade', 'Faturamento'],
    tableRows,
    footnote:
      'Baixas (contabaixa) tipocontaid=1 com cb.deleted_at IS NULL; CAST(data_baixa AS DATE). Série mensal = ano de dataTo; tabela = empresas no mês de dataTo.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData:
      hasChartOrTable(series, tableRows) ||
      (payload.ytdTotal ?? 0) > 0 ||
      (payload.mesAtual ?? 0) > 0,
  };
}

export function buildInadimplenciaIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaInadimplenciaPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const faixas = payload.faixas ?? [];
  const series = faixas.map((row) => ({
    name: row.faixa,
    valor: row.qtd,
    valor2: Math.round(row.saldo),
  }));
  const tableRows = faixas.map((row) => [row.faixa, String(row.qtd), formatCurrency(row.saldo)]);
  return {
    ...base,
    shortDescription: 'Contas a receber vencidas com saldo em aberto (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Saldo vencido',
        value: formatCurrency(payload.saldoVencido ?? 0),
        hint: 'As-of dataTo',
        tone: (payload.saldoVencido ?? 0) > 0 ? 'bad' : 'good',
      },
      {
        label: 'Títulos vencidos',
        value: String(payload.titulosVencidos ?? 0),
        hint: 'As-of dataTo',
        tone: (payload.titulosVencidos ?? 0) > 0 ? 'bad' : 'good',
      },
      {
        label: 'Novos no mês',
        value: String(payload.novosMes ?? 0),
        hint: 'Vencimento no mês de dataTo',
        tone: (payload.novosMes ?? 0) > 0 ? 'bad' : 'good',
      },
    ],
    chartKind: 'barGrouped',
    chartTitle: 'Aging por faixa de atraso',
    series,
    seriesLabels: { valor: 'Qtd.', valor2: 'Saldo' },
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    tableRows,
    footnote:
      'As-of dataTo. Exclui multas (LIKE) e locações com status CANCEL*. Saldo = totalliquido − baixas via JOIN. Aging 0-30 / 31-60 / 61+.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildInfracoesIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaInfracoesPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const pagasMes = payload.pagasMes ?? emptyEstado;
  const vencidas = payload.vencidas ?? emptyEstado;
  const aVencer = payload.aVencer ?? emptyEstado;
  const mensal = payload.mensal ?? [];
  const porEmpresa = payload.porEmpresa ?? [];
  const media = (qtd: number, valor: number) => (qtd > 0 ? valor / qtd : 0);
  const row = (estado: string, qtd: number, valor: number) => [
    estado,
    String(qtd),
    formatCurrency(valor),
    formatCurrency(media(qtd, valor)),
  ];
  const series =
    mensal.length > 0
      ? mensal.map((m) => ({
          name: m.name,
          valor: m.qtdPagas,
          valor2: Math.round(m.valorPagas),
        }))
      : [
          { name: 'Pagas (mês ref.)', valor: pagasMes.qtd, valor2: Math.round(pagasMes.valor) },
          { name: 'Vencidas', valor: vencidas.qtd, valor2: Math.round(vencidas.valor) },
          { name: 'A vencer', valor: aVencer.qtd, valor2: Math.round(aVencer.valor) },
        ];
  const tableRows =
    porEmpresa.length > 0
      ? porEmpresa.slice(0, 20).map((e) => [
          e.nomeEmpresa,
          fmtQtdValor(e.qtdPagas, e.valorPagas ?? 0, formatCurrency),
          fmtQtdValor(e.qtdVencidas, e.valorVencidas ?? 0, formatCurrency),
          fmtQtdValor(e.qtdAVencer, e.valorAVencer ?? 0, formatCurrency),
          formatCurrency(e.valorAberto),
        ])
      : [
          row('Pagas (mês ref.)', pagasMes.qtd, pagasMes.valor),
          row('Vencidas', vencidas.qtd, vencidas.valor),
          row('A vencer', aVencer.qtd, aVencer.valor),
        ];
  const totalQtd = pagasMes.qtd + vencidas.qtd + aVencer.qtd;
  const totalValor = pagasMes.valor + vencidas.valor + aVencer.valor;
  return {
    ...base,
    shortDescription: 'Contas de multa (multa_id) por estado — ERP FlyStart.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Pagas (mês ref.)',
        value: fmtQtdValor(pagasMes.qtd, pagasMes.valor, formatCurrency),
        hint: 'Baixas no ano/mês de dataTo',
        tone: 'good',
      },
      {
        label: 'Vencidas',
        value: fmtQtdValor(vencidas.qtd, vencidas.valor, formatCurrency),
        hint: 'Vencimento < dataTo',
        tone: vencidas.qtd > 0 ? 'bad' : 'good',
      },
      {
        label: 'Média / multa',
        value: formatCurrency(media(totalQtd, totalValor)),
        hint: 'Valor total ÷ qtd. (todos os estados)',
        tone: 'neutral',
      },
    ],
    chartKind: mensal.length > 0 ? 'line' : 'barGrouped',
    chartTitle: mensal.length > 0 ? 'Multas pagas mês a mês' : 'Multas por estado',
    series,
    seriesLabels: { valor: 'Qtd.', valor2: 'Valor' },
    tableHeaders:
      porEmpresa.length > 0
        ? ['Empresa', 'Pagas', 'Vencidas', 'A vencer', 'Aberto']
        : ['Estado', 'Qtd.', 'Valor', 'Média'],
    tableRows,
    footnote:
      'Filtro c.multa_id IS NOT NULL. Saldo: parcela=1 → valorparcela, senão totalliquido − baixas (deleted_at). Pagas = baixas no ano/mês de dataTo. Colunas empresa: qtd · R$. Média = valor ÷ qtd.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: totalQtd > 0,
  };
}

export function buildMotosAlugadasIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaMotosAlugadasPayload
): DiretoriaIndicator {
  const mensal = payload.mensal ?? [];
  const isPeriodMode = payload.variacaoPct !== undefined;
  const novas = payload.novasMes ?? 0;
  const canceladas = payload.canceladasMes ?? 0;
  const series =
    mensal.length > 0
      ? mensal.map((row) => ({ name: row.name, valor: row.novas, valor2: row.canceladas }))
      : [{ name: isPeriodMode ? 'Período' : 'Mês ref.', valor: novas, valor2: canceladas }];
  const tableRows =
    mensal.length > 0
      ? mensal.slice(-6).map((row) => [row.name, String(row.novas), String(row.canceladas)])
      : [
          [isPeriodMode ? 'Novas no período' : 'Novas no mês', String(novas)],
          [isPeriodMode ? 'Canceladas no período' : 'Canceladas no mês', String(canceladas)],
          ['Alugadas (frota)', String(payload.ativas ?? 0)],
        ];
  return {
    ...base,
    shortDescription: 'Frota alugada (ERP) e fluxo de locações (PortalFly).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Alugadas (frota)',
        value: String(payload.ativas ?? 0),
        hint: 'Snapshot · status_equipamento=alugada',
        tone: 'neutral',
      },
      {
        label: isPeriodMode ? 'Novas no período' : 'Novas no mês',
        value: String(novas),
        hint: 'PortalFly · data_inicio_contrato',
        tone: 'good',
      },
      {
        label: isPeriodMode ? 'Canceladas no período' : 'Canceladas no mês',
        value: String(canceladas),
        hint: 'PortalFly · data_cancelamento',
        tone: canceladas > 0 ? 'bad' : 'good',
      },
    ],
    chartKind: 'barGrouped',
    chartTitle: 'Entradas e saídas',
    series,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Novas', valor2: 'Canceladas' },
    tableHeaders: mensal.length > 0 ? ['Mês', 'Entradas', 'Saídas'] : ['Métrica', 'Valor'],
    tableRows,
    footnote:
      'Frota: ERP equipamentos (status_equipamento=alugada). Novas/canceladas: PortalFly locacoes — novas por data_inicio; canceladas por data_cancelamento (excl. ERRO EM/DE CONTRATO).',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}

export function buildRentabilidadeIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaRentabilidadePayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const mensal = payload.mensal ?? [];
  const series = mensal.map((row) => ({
    name: row.name,
    valor: Math.round(row.receita),
    valor2: Math.round(row.custo),
  }));
  const tableRows = [
    ['Receita', formatCurrency(payload.receitaTotal ?? 0)],
    ['Custo', formatCurrency(payload.custoTotal ?? 0)],
    ['Margem %', fmtPct(payload.margemPct ?? null)],
    ['Motos (base)', String(payload.motosBase ?? 0)],
  ];
  return {
    ...base,
    shortDescription: 'Receita (vw_financeiro loc+OS) vs custo atual por moto — snapshot ERP.',
    primaryKpiIndex: 2,
    kpis: [
      {
        label: 'Média receita / moto',
        value: `${formatCurrency(payload.mediaReceitaPorMoto ?? 0)} / moto`,
        hint: 'Snapshot · vw_financeiro RECEBER',
        tone: 'neutral',
      },
      {
        label: 'Média custo / moto',
        value: `${formatCurrency(payload.mediaCustoPorMoto ?? 0)} / moto`,
        hint: 'Snapshot · custo atual',
        tone: (payload.mediaCustoPorMoto ?? 0) < 0 ? 'bad' : 'neutral',
      },
      {
        label: 'Margem',
        value: fmtPct(payload.margemPct ?? null),
        hint: 'Receita − |custo|',
        tone:
          payload.margemPct == null ? 'neutral' : payload.margemPct >= 15 ? 'good' : 'neutral',
      },
    ],
    chartKind: 'line',
    chartTitle: 'Receita vs custo (mensal)',
    series,
    seriesLabels: { valor: 'Receita', valor2: 'Custo' },
    tableHeaders: ['Componente', 'Valor'],
    tableRows,
    footnote:
      'Receita snapshot via vw_financeiro (locação + OS, tipoconta RECEBER). Custo operacional (sem compra). Sem filtro de data na receita canônica.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData:
      hasChartOrTable(series, tableRows) ||
      (payload.receitaTotal ?? 0) !== 0 ||
      (payload.custoTotal ?? 0) !== 0,
  };
}

export function buildLocacaoLocalIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaLocacaoLocalPayload
): DiretoriaIndicator {
  const itens = payload.itens ?? [];
  const byCidade = new Map<string, number>();
  for (const item of itens) {
    byCidade.set(item.cidade, (byCidade.get(item.cidade) ?? 0) + item.quantidade);
  }
  const chartRows = [...byCidade.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, valor]) => ({ name, valor }));
  const tableRows = itens.slice(0, 30).map((row) => [row.bairro, row.cidade, String(row.quantidade)]);

  return {
    ...base,
    shortDescription: 'Locações por bairro/cidade do locatário (ERP FlyStart).',
    primaryKpiIndex: 1,
    kpis: [
      {
        label: 'Cidades',
        value: String(payload.cidadesDistintas ?? 0),
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: 'Top cidade',
        value: payload.topCidade ?? '—',
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
      {
        label: 'Contratos (mês)',
        value: String(payload.contratosMes ?? payload.totalLocacoes ?? 0),
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
    ],
    chartKind: 'barHorizontal',
    chartTitle: 'Top cidades',
    series: chartRows,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Locações' },
    tableHeaders: ['Bairro', 'Cidade', 'Locações'],
    tableRows,
    footnote: 'ERP FlyStart: locacao + pessoa_crm.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: itens.length > 0,
  };
}
